import {
  typedCollection,
  typedDoc,
  addDoc,
  updateDocument,
  deleteDocument,
  serverTimestamp,
  type Timestamp,
} from '@/lib/firebase/firestore'
import {
  storage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from '@/lib/firebase/storage'

export interface MediaDocument {
  id: string
  name: string
  originalName: string
  folderId: string | null
  url: string
  thumbnailUrl?: string
  originalUrl: string
  mimeType: string
  size: number
  originalSize: number
  width: number
  height: number
  status: 'uploading' | 'processing' | 'ready' | 'error'
  uploadedBy: string
  uploadedAt: Timestamp
  updatedAt: Timestamp
}

export type UploadProgressCallback = (pct: number) => void

const mediaCol = () => typedCollection<Omit<MediaDocument, 'id'>>('media')

async function uploadMedia(
  file: File,
  folderId: string | null,
  userId: string,
  onProgress?: UploadProgressCallback
): Promise<string> {
  // Create Firestore doc first so we have an ID for the storage path
  const docRef = await addDoc(mediaCol(), {
    name: file.name,
    originalName: file.name,
    folderId,
    url: '',
    originalUrl: '',
    mimeType: file.type,
    size: 0,
    originalSize: file.size,
    width: 0,
    height: 0,
    status: 'uploading',
    uploadedBy: userId,
    uploadedAt: serverTimestamp() as unknown as Timestamp,
    updatedAt: serverTimestamp() as unknown as Timestamp,
  })

  const mediaId = docRef.id

  try {
    const storageRef = ref(storage, `media/raw/${mediaId}`)
    const task = uploadBytesResumable(storageRef, file)

    const originalUrl = await new Promise<string>((resolve, reject) => {
      task.on(
        'state_changed',
        (snap) => onProgress?.((snap.bytesTransferred / snap.totalBytes) * 100),
        reject,
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref)
            resolve(url)
          } catch (err) {
            reject(err)
          }
        }
      )
    })

    // Mark as processing — Cloud Function will set it to 'ready'
    await updateDocument(typedDoc<MediaDocument>('media', mediaId), {
      status: 'processing',
      originalUrl,
      updatedAt: serverTimestamp() as unknown as Timestamp,
    })
  } catch (err) {
    await updateDocument(typedDoc<MediaDocument>('media', mediaId), {
      status: 'error',
      updatedAt: serverTimestamp() as unknown as Timestamp,
    })
    throw err
  }

  return mediaId
}

async function renameMedia(mediaId: string, name: string): Promise<void> {
  await updateDocument(typedDoc<MediaDocument>('media', mediaId), {
    name,
    updatedAt: serverTimestamp() as unknown as Timestamp,
  })
}

async function moveMedia(mediaId: string, folderId: string | null): Promise<void> {
  await updateDocument(typedDoc<MediaDocument>('media', mediaId), {
    folderId,
    updatedAt: serverTimestamp() as unknown as Timestamp,
  })
}

async function deleteMedia(mediaId: string): Promise<void> {
  await deleteDocument(typedDoc<MediaDocument>('media', mediaId))
  // Best-effort: delete storage files
  try { await deleteObject(ref(storage, `media/raw/${mediaId}`)) } catch { /* may not exist */ }
  try { await deleteObject(ref(storage, `media/processed/${mediaId}`)) } catch { /* may not exist */ }
}

async function saveMediaAsCopy(
  sourceItem: MediaDocument,
  blob: Blob,
  userId: string,
  onProgress?: UploadProgressCallback
): Promise<string> {
  const copyName = `${sourceItem.name} (copy)`
  const docRef = await addDoc(mediaCol(), {
    name: copyName,
    originalName: copyName,
    folderId: sourceItem.folderId,
    url: '',
    originalUrl: '',
    mimeType: 'image/png',
    size: 0,
    originalSize: blob.size,
    width: 0,
    height: 0,
    status: 'uploading',
    uploadedBy: userId,
    uploadedAt: serverTimestamp() as unknown as Timestamp,
    updatedAt: serverTimestamp() as unknown as Timestamp,
  })

  const mediaId = docRef.id

  try {
    const storageRef = ref(storage, `media/raw/${mediaId}`)
    const file = new File([blob], 'cropped.png', { type: blob.type || 'image/png' })
    const task = uploadBytesResumable(storageRef, file)

    const originalUrl = await new Promise<string>((resolve, reject) => {
      task.on(
        'state_changed',
        (snap) => onProgress?.((snap.bytesTransferred / snap.totalBytes) * 100),
        reject,
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref)
            resolve(url)
          } catch (err) {
            reject(err)
          }
        }
      )
    })

    await updateDocument(typedDoc<MediaDocument>('media', mediaId), {
      status: 'processing',
      originalUrl,
      updatedAt: serverTimestamp() as unknown as Timestamp,
    })
  } catch (err) {
    await updateDocument(typedDoc<MediaDocument>('media', mediaId), {
      status: 'error',
      updatedAt: serverTimestamp() as unknown as Timestamp,
    })
    throw err
  }

  return mediaId
}

async function overwriteMediaWithCrop(
  mediaId: string,
  blob: Blob,
  onProgress?: UploadProgressCallback
): Promise<void> {
  await updateDocument(typedDoc<MediaDocument>('media', mediaId), {
    status: 'processing',
    updatedAt: serverTimestamp() as unknown as Timestamp,
  })

  const storageRef = ref(storage, `media/raw/${mediaId}`)
  const file = new File([blob], 'cropped.png', { type: blob.type || 'image/png' })
  const task = uploadBytesResumable(storageRef, file)

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => onProgress?.((snap.bytesTransferred / snap.totalBytes) * 100),
      reject,
      resolve
    )
  })
}

export const adminMediaService = {
  uploadMedia,
  renameMedia,
  moveMedia,
  deleteMedia,
  saveMediaAsCopy,
  overwriteMediaWithCrop,
}
